/**
 * passerelle-a2-a-interactif.js
 *
 * Couche INTERACTIVE pour la Passerelle A2→A (et trame générique).
 *
 * Pour chaque étape du cours, on ajoute :
 *   - briseGlace : 1 question d'icebreaker (réflexion ouverte) à projeter
 *   - qcm        : 1 QCM 4 options + bonne réponse + explication
 *   - statClef   : 1 chiffre-choc à révéler en animation
 *   - videoUrl   : (optionnel) URL d'une vidéo YouTube à embarquer
 *
 * L'objectif : transformer un long scroll passif en présentation rythmée
 * avec interaction toutes les 5-10 minutes.
 */

export const PASSERELLE_INTERACTIF = {
  briseGlaceOuverture: {
    titre: '🔥 Brise-glace — Avant de commencer',
    consigne: 'Lève la main pour répondre',
    question: 'En passant de 35 kW à 70 kW, ta moto va prendre… combien de plus en accélération ?',
    options: [
      { label: '×1,5 fois', value: 1 },
      { label: '×2 fois', value: 2, correct: true },
      { label: '×3 fois', value: 3 },
      { label: '×5 fois', value: 5 },
    ],
    explication:
      '×2. La puissance double mais le poids reste similaire. Concrètement : ton 0-100 km/h passe sous les 4 secondes — plus rapide que la quasi-totalité des voitures de série.',
  },

  // 1 entrée par étape du deroule (8 étapes)
  etapes: [
    /* Étape 1 — Tour de table */
    {
      etape: 1,
      briseGlace:
        'Dis ton prénom + UNE peur que tu as sur le passage à 70 kW. Les peurs nommées sont les peurs apprivoisées.',
      statClef: { valeur: '94%', label: 'des motards passent A après A2 sans formation', detail: 'Avant 2020 — c\'est ce que la Passerelle vient combler.' },
    },

    /* Étape 2 — Statistiques accidentologie */
    {
      etape: 2,
      qcm: {
        question: 'Quel pourcentage de la mortalité routière représentent les motards en France ?',
        options: [
          { label: '5 %', correct: false },
          { label: '12 %', correct: false },
          { label: '23 %', correct: true },
          { label: '40 %', correct: false },
        ],
        explication:
          '23 % des morts sur la route sont des motards alors que la moto représente moins de 2 % du trafic. Source : ONISR 2024.',
      },
      statClef: { valeur: '×11', label: 'plus de risque de mourir à moto vs en voiture', detail: '(par km parcouru, ONISR 2024)' },
      videoUrl: 'https://www.youtube.com/watch?v=Y0M8WMlPB9I', // Sécurité Routière — campagne moto (à adapter)
      videoCaption: 'Discussion à chaud : avez-vous déjà vécu ou été témoin d\'une scène similaire ? Qu\'est-ce qui a manqué pour éviter le pire ?',
    },

    /* Étape 3 — Vitesse / Alcool / Équipement */
    {
      etape: 3,
      qcm: {
        question: 'À 100 km/h, ton champ visuel est réduit à environ…',
        options: [
          { label: '90°', correct: false },
          { label: '60°', correct: false },
          { label: '45°', correct: true },
          { label: '120°', correct: false },
        ],
        explication:
          '45°. Tu perds plus de 75 % de ton champ visuel par rapport à l\'arrêt (180°). C\'est ce qu\'on appelle "l\'effet tunnel".',
      },
      statClef: { valeur: '180° → 30°', label: 'Ton champ visuel à 120 km/h', detail: 'Tu vois "comme dans un tunnel" — angle plus étroit que tes phares.' },
    },

    /* Étape 4 — Freinage / Slalom / Évitement */
    {
      etape: 4,
      qcm: {
        question: 'En freinage d\'urgence, quel est le pourcentage de l\'efficacité APPORTÉE par le frein AVANT seul ?',
        options: [
          { label: '30 %', correct: false },
          { label: '50 %', correct: false },
          { label: '70 %', correct: true },
          { label: '95 %', correct: false },
        ],
        explication:
          '70 %. Mais utiliser uniquement le frein avant = risque de blocage et chute. La règle d\'or : LES 2 FREINS COMBINÉS, toujours.',
      },
      statClef: { valeur: '2 freins', label: 'La règle d\'or du freinage', detail: 'AV + AR combinés. Toujours. Sans exception.' },
    },

    /* Étape 5 — Études de cas commentées */
    {
      etape: 5,
      briseGlace:
        'Cite UN accident de moto que tu as vu (réel ou vidéo). Qu\'est-ce qui a causé l\'accident selon toi ?',
      qcm: {
        question: 'Tu arrives à une intersection sans visibilité. Quelle est ta MEILLEURE stratégie ?',
        options: [
          { label: 'Ralentir et anticiper la priorité à droite', correct: true },
          { label: 'Klaxonner pour signaler ta présence', correct: false },
          { label: 'Accélérer pour traverser vite', correct: false },
          { label: 'Mettre les warnings et passer', correct: false },
        ],
        explication:
          'Ralentir + anticiper la priorité à droite (règle générale). Le klaxon ne sauve personne — la vitesse adaptée et la couverture des freins, OUI.',
      },
    },

    /* Étape 6 — Plateau */
    {
      etape: 6,
      briseGlace:
        'Sur quel exercice plateau penses-tu être le moins à l\'aise aujourd\'hui ? Slalom, demi-tour, freinage d\'urgence, évitement ?',
      statClef: { valeur: '4 doigts', label: 'sur le levier de frein en formation', detail: 'Pas 2 doigts : pour assurer la force et l\'amplitude du freinage.' },
    },

    /* Étape 7 — Circulation */
    {
      etape: 7,
      qcm: {
        question: 'Quels sont les 2 critères de sécurité à vérifier AVANT chaque départ en moto ?',
        options: [
          { label: 'Vitesse + Trajectoire', correct: false },
          { label: 'Adhérence + Visibilité', correct: true },
          { label: 'Équipement + Pneus', correct: false },
          { label: 'Distance + Allure', correct: false },
        ],
        explication:
          'Adhérence + Visibilité. Ce sont les 2 critères pédagogiques fondamentaux du REMC. Tout le reste en découle.',
      },
      statClef: { valeur: '2 sec', label: 'Distance de sécurité minimum', detail: 'Au moins 2 secondes entre toi et le véhicule devant — plus en cas de pluie.' },
    },

    /* Étape 8 — Clôture */
    {
      etape: 8,
      briseGlace:
        'En UNE phrase : qu\'est-ce que tu retiens de ces 7 heures que tu vas appliquer DÈS demain ?',
      statClef: { valeur: '100%', label: 'des accidents évitables commencent par une décision', detail: 'Tu repars avec ton plan personnel. Le reste, c\'est toi qui le joues.' },
    },
  ],

  // Citation finale à projeter en grand pour clôture
  citationFinale: {
    texte: 'On ne devient pas motard en passant un examen. On devient motard en survivant aux 6 premiers mois.',
    auteur: 'Adage moto-école',
  },
};

export default PASSERELLE_INTERACTIF;
